<template>
  <TableCell class="KeypadSetting"
    :tag="tag"
    :popoverDisabled="readOnly"
    :popoverTitle="popoverTitle"
    :selectedCells="selectedCells"
    v-model:showingPopover="showingPopover"
    v-model:columnSelectionCode="columnSelectionCode"
  >
    <slot>
      <template v-if="cellShowingRelativeValue">
        <span style="font-weight: 200;">
          {{ prefix }} {{ cellConvertedValue >= 0 ? '+' : '-' }}
        </span>
        {{ cellConvertedValueStringAbsolute }}
        <span v-html="convertedUnitHtml(unit)" style="font-weight: 200;"></span>
      </template>
      <template v-else>
        {{ cellConvertedValueString }}
        <span v-html="convertedUnitHtml(unit)" style="font-weight: 200;"></span>
      </template>
    </slot>

    <template #BinSelectionInput>
      <slot name="BinSelectionInput"></slot>
    </template>

    <template #PopoverContentDesktop="{helpText}">
      <div class="keypadSetting-popoverContent">
        <h3 class="popoverTitle" v-html="popoverTitle" style="height: 43px; display: flex; align-items: center;"></h3>
        <slot name="HelpText" :helpText>
          <div v-if="helpText" class="helpText" v-html="processHelpText(helpText)"></div>
        </slot>
        <div class="checkboxes">
          <slot name="Checkboxes" :showingToggleRelativeValues>
            <Checkbox v-if="showingToggleRelativeValues" v-model="store.showingRelativeValues" binary>
              Show relative values
            </Checkbox>
            <slot name="BinSelectionInput"></slot>
          </slot>
        </div>
        <div class="buttons">
          <Button v-if="showingDisableSetting" severity="secondary" @click="onClickDisableSetting()" style="align-self: stretch;">
            <i class="pi pi-wrench" style="font-size: small; margin-right: 10px;"></i>
            Disable setting
          </Button>
          <slot name="Buttons"></slot>
        </div>
        <Keypad class="keypad"
          v-model:value="popoverConvertedValue"
          v-model:edited="didEditKeypadValue"
          :range="popoverConvertedRange"
          :prefix="popoverPrefix"
          :suffix="convertedUnitHtml(unit)"
          :formatter="formatValue"
          :validator="validator"
          @ok="value => submit(value)" 
          @cancel="showingPopover = false"
          @keypress="key => $emit('keypress', key)"
          @erase="$emit('erase')"
        >
          <template #InputBox>
            <slot name="InputBox"></slot>
          </template>
        </Keypad>
      </div>
    </template>

  </TableCell>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import store from "./store.js";
import { roundToNearestFixedRange } from "./utility.js";
import { convertValue, convertUnit, convertedUnitHtml, unconvertValue, unitRound } from "./units";
import { changeSettings, toggleOptionalSettings } from "./appUtility";
import TableCell from "./TableCell.vue";
import Keypad from "./Keypad.vue";

let props = defineProps([
  "tag",
  "prefix",
  "unit",
  "readOnly",
  "reference",
  "referenceName",
  "disableInvertKey",
  "disableAbsoluteValue",
  "showingPopover",
  "popoverTitle",
  "popoverPrefix",
  "popoverReference",
  "popoverDisableAbsoluteValue",
  "columnSelectionCode",
  "selectedCells",
  "getSubmitPopoverReferenceTag",
  "getSubmitValues",
  "validator"
]);

let emit = defineEmits([
  "keypress",
  "erase"
]);

let showingPopover = defineModel("showingPopover");
let columnSelectionCode = defineModel("columnSelectionCode");

let unit = computed(() => props.unit ?? props.tag.$unit());

let referenceTag = computed(() => {
  return props.reference ? props.tag.$session.system.$atPath(props.reference) : null;
});

let cellShowingRelativeValue = computed(() => {
  if (props.disableAbsoluteValue)
    return true;
  else
    return (
      store.showingRelativeValues && 
      referenceTag.value != null &&
      referenceTag.value != props.tag
    );
});

let popoverReference = computed(() => {
  if (props.popoverReference === undefined)
    return props.reference;
  else
    return props.popoverReference;
});

let popoverReferenceTag = computed(() => {
  if (popoverReference.value == null)
    return null;
  else
    return props.tag.$session.system.$atPath(popoverReference.value);
});

let popoverDisableAbsoluteValue = computed(() => {
  return props.popoverDisableAbsoluteValue ?? props.disableAbsoluteValue;
});

const popoverShowingRelativeValue = computed(() => {
  const isDifferentialValue = unit.value?.includes("-diff");
  const hasReferenceTag = (
    popoverReferenceTag.value != null &&
    popoverReferenceTag.value != props.tag
  );

  // Always show the relative value if
  //  1) Absolute values are disabled for this setting, or
  //  2) Showing relative values for all settings, and this setting has either a differential value natively, or a reference tag.
  //  3) Not showing relative values for all settings, but this setting has a differential value natively and no reference tag from which to compute an absolute value.
  return (
    popoverDisableAbsoluteValue.value ||
    (store.showingRelativeValues && (isDifferentialValue || hasReferenceTag)) ||
    (!store.showingRelativeValues && (isDifferentialValue && !hasReferenceTag))
  );
});

let popoverPrefix = computed(() => {
  if (!popoverShowingRelativeValue.value)
    return null;
  else if (props.popoverPrefix === undefined)
    return props.prefix;
  else
    return props.popoverPrefix;
});

let showingToggleRelativeValues = computed(() => {
  return (
    popoverReferenceTag.value &&
    popoverReferenceTag.value != props.tag &&
    !popoverDisableAbsoluteValue.value
  );
});

let showingDisableSetting = computed(() => (
  store.showAdvanced &&
  props.tag.settingOptional &&
  props.tag.settingEnabled
));

const cellConvertedValue = computed(() => {
  if (typeof props.tag.$value() == "string")
    return props.tag.$value();
  else
    return convertBasisAndUnit(
      props.tag.$value(),
      unit.value,
      referenceTag.value?.$value(),
      referenceTag.value?.$unit(),
      cellShowingRelativeValue.value
    );
});

const cellConvertedValueString = computed(() => {
  if (typeof cellConvertedValue.value == "string")
    return cellConvertedValue.value;
  else
    return formatValue(cellConvertedValue.value);
});

const cellConvertedValueStringAbsolute = computed(() => {
  if (typeof cellConvertedValue.value == "string")
    return cellConvertedValue.value;
  else
    return formatValue(Math.abs(cellConvertedValue.value));
});

function formatValue(convertedValue) {
  return roundToNearestFixedRange(
    convertedValue,
    props.tag.$convertedMinPrecision(),
    props.tag.$convertedMaxPrecision()
  );
}

let popoverInitialConvertedValue = computed(() => {
  const tagValue = props.tag.$value();
  const tagUnit = unit.value;
  const referenceValue = popoverReferenceTag.value?.$value();
  const isTagRelative = unit.value?.includes("-diff");
  const showingRelativeValue = popoverShowingRelativeValue.value;

  if (isTagRelative && showingRelativeValue)
    return convertValue(tagValue, tagUnit);
  if (isTagRelative && !showingRelativeValue)
    return convertValue(tagValue + referenceValue, tagUnit.replace("-diff", ""));
  if (!isTagRelative && showingRelativeValue)
    return convertValue(tagValue - referenceValue, `${tagUnit}-diff`);
  if (!isTagRelative && !showingRelativeValue)
    return convertValue(tagValue, tagUnit);
});

let didEditKeypadValue = ref(false);
let popoverConvertedValue = ref(popoverInitialConvertedValue.value);

// Reset the popover value when the popover is displayed.
watch(showingPopover, () => {
  didEditKeypadValue.value = false;
  popoverConvertedValue.value = popoverInitialConvertedValue.value;
});

// Convert the keypad value when toggling between relative and absolute values.
watch([popoverReference, popoverShowingRelativeValue], (newVals, oldVals) => {
  didEditKeypadValue.value = false;

  const [newReference, newShowingRelativeValue] = newVals;
  const [oldReference, oldShowingRelativeValue] = oldVals;
  const didPopoverReferenceChange = (newReference != oldReference);
  const didShowingRelativeValueChange = (newShowingRelativeValue != oldShowingRelativeValue);

  // If the both the reference and viewing mode changed, just reset the value.
  if (didPopoverReferenceChange && didShowingRelativeValueChange) {
    popoverConvertedValue.value = popoverInitialConvertedValue.value;
  }
  // Otherwise, convert the value according to it's new reference.
  else if (didShowingRelativeValueChange) {
    const referenceValue = popoverReferenceTag.value.$value();
    const referenceUnit = unit.value.replace("-diff", "");
    const convertedReferenceValue = convertValue(referenceValue, referenceUnit);
    if (popoverShowingRelativeValue.value) {
      const absoluteValue = popoverConvertedValue.value;
      const relativeValue = absoluteValue - convertedReferenceValue;
      popoverConvertedValue.value = relativeValue;
    }
    else {
      const relativeValue = popoverConvertedValue.value;
      const absoluteValue = convertedReferenceValue + relativeValue;
      popoverConvertedValue.value = absoluteValue;
    }
  }
});

let popoverConvertedRange = computed(() => {
  if (!props.tag.range) return null;
  let convertedMin = convertBasisAndUnit(
    props.tag.range[0],
    unit.value,
    popoverReferenceTag.value?.$value(),
    popoverReferenceTag.value?.$unit(),
    popoverShowingRelativeValue.value
  );
  let convertedMax = convertBasisAndUnit(
    props.tag.range[1],
    unit.value,
    popoverReferenceTag.value?.$value(),
    popoverReferenceTag.value?.$unit(),
    popoverShowingRelativeValue.value
  );
  return [
    convertedMin,
    convertedMax
  ];
});

function processHelpText(helpText) {
  let keypadStringRegex = /\$keypadString/g;
  let keypadStringReplacer = (match) => {
    let valueString = `${formatValue(popoverShowingRelativeValue.value ? Math.abs(popoverConvertedValue.value) : popoverConvertedValue.value)} ${convertedUnitHtml(unit.value)}`;
    if (popoverShowingRelativeValue.value && popoverReferenceTag.value) {
      let convertedReferenceValue = convertValue(popoverReferenceTag.value.$value(), popoverReferenceTag.value.$unit());
      let popoverAbsoluteConvertedValue = convertedReferenceValue + popoverConvertedValue.value;
      if (popoverAbsoluteConvertedValue >= convertedReferenceValue)
        return `${valueString} above the ${props.referenceName}`;
      else
        return `${valueString} below the ${props.referenceName}`;
    }
    else {
      return valueString;
    }
  };

  let keypadValueRegex = /\$keypadValue/g;
  let keypadValueReplacer = (match) => {
    return `${formatValue(popoverConvertedValue.value)} ${convertedUnitHtml(unit.value)}`;
  };

  let absKeypadValueRegex = /\$absKeypadValue/g;
  let absKeypadValueReplacer = (match) => {
    return `${formatValue(Math.abs(popoverConvertedValue.value))} ${convertedUnitHtml(unit.value)}`;
  };

  let ifRelativeRegex = /\$ifRelative\('(.*?)'\, '(.*?)'\)/g
  let ifRelativeReplacer = (match, stringWhenRelative, stringWhenNotRelative) => {
    return popoverShowingRelativeValue.value ? stringWhenRelative : stringWhenNotRelative;
  };

  let signRegex = /\$sign\('(.*?)'\, '(.*?)'\)/g;
  let signReplacer = (match, stringWhenPositive, stringWhenNegative) => {
    return popoverConvertedValue.value >= 0 ? stringWhenPositive : stringWhenNegative;
  };

  let strings = (helpText.startsWith("[") && helpText.endsWith("]")) ? JSON.parse(helpText) : [ helpText ];
  let substringRegex = /\$s(\d)/g;
  let substringReplacer = (match, substringNumber) => {
    let index = substringNumber - 1;
    return strings[index];
  };

  for (let i = strings.length - 1; i >= 0; i--) {
    strings[i] = (strings[i]
      .replace(substringRegex, substringReplacer)
      .replace(keypadStringRegex, keypadStringReplacer)
      .replace(keypadValueRegex, keypadValueReplacer)
      .replace(absKeypadValueRegex, absKeypadValueReplacer)
      .replace(ifRelativeRegex, ifRelativeReplacer)
      .replace(signRegex, signReplacer)
    );
  }

  return strings[0];
}

function submit(convertedValue) {
  showingPopover.value = false;
  
  if (props.selectedCells) {
    changeSettings(props.selectedCells, (cell) => {
      let tag = cell.componentArgs.tag;
      let convertedUnit = convertUnit(unit.value);
      let referenceTag = getSubmitPopoverReferenceTag(cell, tag);
      let unconvertedValue = unconvertBasisAndUnit(convertedValue, convertedUnit, referenceTag?.$value(), referenceTag?.$unit(), popoverShowingRelativeValue.value);
      return getSubmitValues(cell, unconvertedValue);
    });
  }
  else {
    let tag = props.tag;
    let session = props.tag.$session;
    let convertedUnit = convertUnit(unit.value);
    let unconvertedValue = unconvertBasisAndUnit(convertedValue, convertedUnit, null, null, false);
    session.changeSettings([{ handle: tag.handle, value: unconvertedValue }]);
  }
}

function onClickDisableSetting() {
  showingPopover.value = false;
  toggleOptionalSettings(props.selectedCells, false);
}

function convertBasisAndUnit(value, unit, referenceValue, referenceUnit, showingRelativeValue) {
  if (typeof value == "string") {
    value = parseFloat(value);
  }
  
  let convertedValue = convertValue(value, unit);
  let hasReferenceValue = (referenceValue != null);

  // Convert to relative if necessary.
  let mustConvertToRelative = (showingRelativeValue && unit != "fahrenheit-diff" && hasReferenceValue);
  if (mustConvertToRelative) {
    let convertedReferenceValue = convertValue(referenceValue, referenceUnit);
    let difference = convertedValue - convertedReferenceValue;
    return difference;
  }

  // Otherwise, convert to absolute if necessary.
  let mustConvertToAbsolute = (!showingRelativeValue && unit == "fahrenheit-diff" && hasReferenceValue);
  if (mustConvertToAbsolute) {
    let convertedReferenceValue = convertValue(referenceValue, "fahrenheit");
    let absoluteValue = convertedReferenceValue + convertedValue;
    return absoluteValue;
  }

  // Otherwise, no basis conversion required.
  return convertedValue;
}

function unconvertBasisAndUnit(convertedValue, convertedUnit, referenceValue, referenceUnit, showingRelativeValue) {
  let hasReferenceValue = (referenceValue != null);
  
  let mustUnconvertFromRelative = (showingRelativeValue && unit.value != "fahrenheit-diff" && hasReferenceValue);
  if (mustUnconvertFromRelative) {
    let convertedReferenceValue = convertValue(referenceValue, referenceUnit);
    let absValue = convertedReferenceValue + convertedValue;
    return unconvertValue(absValue, convertedUnit);
  }

  let mustUnconvertFromAbsolute = (!showingRelativeValue && unit.value == "fahrenheit-diff" && hasReferenceValue);
  if (mustUnconvertFromAbsolute) {
    let convertedReferenceValue = convertValue(referenceValue, referenceUnit);
    let relativeValue = convertedValue - convertedReferenceValue;
    return unconvertValue(relativeValue, convertedUnit);
  }

  return unconvertValue(convertedValue, convertedUnit);  
}

function getSubmitPopoverReferenceTag(cell, tag) {
  if (props.getSubmitPopoverReferenceTag) {
    return props.getSubmitPopoverReferenceTag(cell, tag);
  }
  else {
    let system = tag.$session.system;
    let reference = (tag == props.tag) ? popoverReference.value : cell.componentArgs.reference;
    let referenceTag = reference == null ? null : system.$atPath(reference);
    return referenceTag;
  }
}

function getSubmitValues(cell, unconvertedValue) {
  if (props.getSubmitValues) {
    return props.getSubmitValues(cell, unconvertedValue);
  }
  else {
    return [{
      handle: cell.componentArgs.tag.handle,
      value: unconvertedValue
    }];
  }
}
</script>

<style>
td.KeypadSetting {
  white-space: nowrap;
}

.mobile .keypadSetting-popoverContent {
  /* width: calc(100vw - 60px); */
  display: grid;
  gap: 10px;
  grid-template-columns: auto;
  grid-template-rows: auto auto;
  grid-template-areas: 
    "popoverTitle"
    "helpText"
    "checkboxes"
    "keypad"
    "buttons";
}
.mobile .keypadSetting-popoverContent .popoverTitle {
  grid-area: popoverTitle;
  width: 100%;
  justify-content: center;
}
.mobile .keypadSetting-popoverContent .keypad {
  grid-area: keypad;
}
.mobile .keypadSetting-popoverContent .helpText {
  grid-area: helpText;
  margin-bottom: 8px;
  max-width: 380px;
}
.mobile .keypadSetting-popoverContent .checkboxes {
  grid-area: checkboxes;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.mobile .keypadSetting-popoverContent .buttons {
  grid-area: buttons;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
}

.desktop .keypadSetting-popoverContent {
  width: 575px;
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto 1fr auto auto;
  grid-template-areas: 
    "popoverTitle keypad"
    "helpText keypad"
    "checkboxes keypad"
    "buttons keypad";
}
.desktop .keypadSetting-popoverContent .popoverTitle {
  grid-area: popoverTitle;
}
.desktop .keypadSetting-popoverContent .keypad {
  grid-area: keypad;
}
.desktop .keypadSetting-popoverContent .helpText {
  grid-area: helpText;
}
.desktop .keypadSetting-popoverContent .checkboxes {
  grid-area: checkboxes;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
}
.desktop .keypadSetting-popoverContent .buttons {
  margin-right: 50px;
  grid-area: buttons;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
}
</style>